import { useState, useEffect } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints, ApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';

interface SubscriptionAccessStatus {
  hasAccess: boolean;
  accessType: 'subscription' | 'trial' | 'org_override' | 'free' | 'none' | 'error';
  loading: boolean;
  error: string | null;
  details?: any;
}

export const useSubscriptionAccess = () => {
  const api = useAuthenticatedApi();
  const { checkAuth, isLoading, isAuthenticated } = useAuth();
  const { currentOrganization } = useOrganization();
  const [status, setStatus] = useState<SubscriptionAccessStatus>({
    hasAccess: false,
    accessType: 'none',
    loading: true,
    error: null,
    details: {}
  });

  useEffect(() => {
    let isCancelled = false;

    const checkAccess = async () => {
      // Wait for Clerk to finish loading before making API calls
      if (isLoading) {
        return;
      }

      // If not authenticated, no access
      if (!isAuthenticated) {
        setStatus({
          hasAccess: false,
          accessType: 'none',
          loading: false,
          error: null,
          details: {}
        });
        return;
      }

      // Access is organization-scoped; without an active org context we cannot grant access.
      if (!currentOrganization?.id) {
        setStatus({
          hasAccess: false,
          accessType: 'none',
          loading: false,
          error: null,
          details: {}
        });
        return;
      }

      if (currentOrganization.is_demo) {
        setStatus({
          hasAccess: true,
          accessType: 'free',
          loading: false,
          error: null,
          details: { demo: true }
        });
        return;
      }

      if (!api) {
        setStatus(prev => ({
          ...prev,
          hasAccess: false,
          accessType: 'none',
          loading: false,
          error: null,
          details: {}
        }));
        return;
      }

      // Org switches must immediately invalidate stale access from the previous org.
      setStatus(prev => ({
        ...prev,
        hasAccess: false,
        accessType: 'none',
        loading: true,
        error: null,
        details: {}
      }));

      try {
        const response = await api.get(apiEndpoints.billing.hasSubscriptionAccess);

        if (isCancelled) {
          return;
        }

        setStatus({
          hasAccess: response.has_access || false,
          accessType: response.access_type || 'none',
          loading: false,
          error: null,
          details: response.details || {}
        });
      } catch (error) {
        if (isCancelled) {
          return;
        }

        console.error('Failed to check subscription access:', error);
        // If unauthorized, surface an auth error so the guard can redirect to login instead of subscription page
        if (error instanceof ApiError && error.status === 401) {
          // Attempt to refresh local auth state; do not navigate here (handled by guard)
          checkAuth().catch(() => {});
          setStatus({
            hasAccess: false,
            accessType: 'error',
            loading: false,
            error: 'auth',
            details: {}
          });
          return;
        }
        setStatus({
          hasAccess: false,
          accessType: 'error',
          loading: false,
          error: 'Failed to verify access status',
          details: {}
        });
      }
    };

    checkAccess();
    return () => {
      isCancelled = true;
    };
  }, [
    api,
    checkAuth,
    isLoading,
    isAuthenticated,
    currentOrganization?.id,
    currentOrganization?.is_demo,
  ]);

  return status;
};
